import { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

// Use unified date utils
import { moment } from '../utils/dateUtils';

import {
  Box,
  Typography,
  Card,
  CardContent,
  Tooltip,
  Grid,
  Chip,
  Paper,
  useMediaQuery,
  useTheme,
  IconButton,
  Button,
} from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';
import NavigationIcon from '@mui/icons-material/Navigation';
import PrintIcon from '@mui/icons-material/Print';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import Inventory2Icon from '@mui/icons-material/Inventory2';

// Icons
import PersonIcon from '@mui/icons-material/Person';
import PeopleIcon from '@mui/icons-material/People';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

// Custom Components
import { Spinner, BrowserTitle, DateSwitcher } from '../components';
import ViewSwitcher from '../components/Appointments/ViewSwitcher';
import OrderEquipmentModal from '../components/Proposal/OrderEquipmentModal';
import CheckEquipmentModal from '../components/CheckEquipmentModal';

// Hooks
import { useInstallationSocket } from '../hooks/useSocket';

// Utils
import messages from '../static/messages';

// Services
import installationScheduleServices from '../services/installationScheduleServices';
import proposalsServices from '../services/proposalsServices';
import integrationServices from '../services/integrationServices';

// moment.tz.setDefault is now configured in dateUtils.js

// Time badge colors
const timeColors = {
  'AM': { bg: '#e3f2fd', color: '#1565c0' },
  'PM': { bg: '#fff3e0', color: '#ef6c00' },
  'Full Day': { bg: '#f3e5f5', color: '#7b1fa2' },
};

// Check if proposal has equipment in any system
const proposalHasEquipment = (proposal) => {
  return proposal?.proposalSystems?.some(s => s.equipments?.length > 0);
};

const isWarrantyProposal = (proposal) => !!proposal?.warrantyTicket;

const getWarehousePO = (installation) => {
  const proposal = installation?.proposal;
  if (isWarrantyProposal(proposal) && proposal.warrantyTicket.ticketNumber) {
    return proposal.warrantyTicket.ticketNumber;
  }
  return proposal?.publicId;
};

// Check if all equipment has been ordered (via checkbox in Order Equipment modal)
const isAllEquipmentOrdered = (proposal) => {
  return proposal?.additionalNotes?.orderEquipmentCompleted?.completed === true;
};

// Installation Card Component
const InstallationCard = ({ installation, onClick, isMobile, onOrderClick, onCheckClick, serialStatus }) => {
  const { proposal } = installation;
  const isLegacy = !!installation._isLegacy;
  const property = proposal?.property;
  const client = property?.clients?.[0]?.client;
  const salesman = proposal?.createdBy;
  const jobOverview = proposal?.jobOverview;
  const timeColor = timeColors[installation.installationTime] || timeColors['AM'];
  const hasEquipment = isLegacy ? false : proposalHasEquipment(proposal);
  const allOrdered = hasEquipment && isAllEquipmentOrdered(proposal);
  const isWarranty = !isLegacy && isWarrantyProposal(proposal);
  const showWarehouseIcon = isLegacy || hasEquipment || isWarranty;

  // Get phone number from client
  const phoneNumber = client?.phoneNumber || client?.mobile;
  
  // Open Google Maps with address
  const handleNavigate = (e) => {
    e.stopPropagation();
    const address = property?.address;
    if (address) {
      window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`, '_blank');
    }
  };

  // Call phone number
  const handleCall = (e) => {
    e.stopPropagation();
    if (phoneNumber) {
      window.location.href = `tel:${phoneNumber}`;
    }
  };

  const handleCardClick = () => {
    if (isLegacy) {
      if (installation.legacyUrl) {
        window.open(installation.legacyUrl, '_blank');
      }
    } else {
      onClick(proposal?.id);
    }
  };

  const cardContent = (
    <Card
      sx={{
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        border: isLegacy ? '1px solid #e65100' : '1px solid #e0e0e0',
        backgroundColor: isLegacy ? '#fff8e1' : '#f8f9fa',
        '&:hover': {
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          transform: isMobile ? 'none' : 'translateY(-2px)',
          borderColor: isLegacy ? '#bf360c' : '#1976d2',
          backgroundColor: isLegacy ? '#fff3e0' : '#fff',
        },
        '&:active': {
          transform: isMobile ? 'scale(0.98)' : 'translateY(-2px)',
        },
      }}
      onClick={handleCardClick}
    >
      <CardContent sx={{ p: isMobile ? 1.5 : 2, '&:last-child': { pb: isMobile ? 1.5 : 2 } }}>
        {/* PO Number, Equipment Icons & Time */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 700,
                color: isLegacy ? '#e65100' : '#1976d2',
                fontFamily: 'monospace',
                fontSize: isMobile ? '1rem' : '1.1rem',
              }}
            >
              {proposal?.publicId || 'N/A'}
            </Typography>
            {isLegacy && (
              <Chip
                label="LEGACY"
                size="small"
                sx={{
                  backgroundColor: '#e65100',
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: '0.6rem',
                  height: 20,
                  ml: 0.5,
                }}
              />
            )}
            {hasEquipment && !isLegacy && (
              <Tooltip title={allOrdered ? 'All equipment ordered' : 'Order Equipment'} arrow>
                <IconButton
                  size="small"
                  onClick={(e) => { e.stopPropagation(); onOrderClick?.(installation); }}
                  sx={{ p: 0.5 }}
                >
                  <LocalShippingIcon sx={{ fontSize: 18, color: allOrdered ? '#4caf50' : '#9e9e9e' }} />
                </IconButton>
              </Tooltip>
            )}
            {showWarehouseIcon && (
              <Tooltip title={serialStatus === 'all' ? 'All equipment has serial numbers' : serialStatus === 'partial' ? 'Some equipment missing serial numbers' : 'Check Equipment'} arrow>
                <IconButton
                  size="small"
                  onClick={(e) => { e.stopPropagation(); onCheckClick?.(installation); }}
                  sx={{ p: 0.5 }}
                >
                  <Inventory2Icon sx={{ fontSize: 18, color: serialStatus === 'all' ? '#4caf50' : serialStatus === 'partial' ? '#ff9800' : '#9e9e9e' }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {installation.installationTime && (
              <Chip
                label={installation.installationTime}
                size="small"
                sx={{
                  backgroundColor: timeColor.bg,
                  color: timeColor.color,
                  fontWeight: 600,
                  fontSize: '0.75rem',
                }}
              />
            )}
            {installation.installersCount != null && (
              <Tooltip title={`${installation.installersCount} Installer(s)`} arrow>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, color: '#666' }}>
                  <PeopleIcon sx={{ fontSize: 16 }} />
                  <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.75rem', lineHeight: 1 }}>
                    {installation.installersCount}
                  </Typography>
                </Box>
              </Tooltip>
            )}
            {installation.trucksCount != null && (
              <Tooltip title={`${installation.trucksCount} Truck(s)`} arrow>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, color: '#666' }}>
                  <LocalShippingIcon sx={{ fontSize: 16 }} />
                  <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.75rem', lineHeight: 1 }}>
                    {installation.trucksCount}
                  </Typography>
                </Box>
              </Tooltip>
            )}
          </Box>
        </Box>

        {/* Salesman - hide on mobile for technicians (they see only their own) */}
        {!isMobile && !isLegacy && (
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <PersonIcon sx={{ fontSize: 18, color: '#666', mr: 1 }} />
            <Typography variant="body2" sx={{ color: '#333' }}>
              {salesman?.fullName || 'Unknown'}
            </Typography>
          </Box>
        )}

        {/* Address */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
          <LocationOnIcon sx={{ fontSize: 18, color: '#666', mr: 1, mt: 0.3, flexShrink: 0 }} />
          <Typography 
            variant="body2" 
            sx={{ 
              color: '#333', 
              lineHeight: 1.4,
              fontSize: isMobile ? '0.85rem' : '0.875rem',
            }}
          >
            {property?.address || 'No address'}
          </Typography>
        </Box>

        {/* Client Name */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: isMobile ? 1.5 : 0 }}>
          <Typography 
            variant="body2" 
            sx={{ 
              color: '#666', 
              fontWeight: 500,
              fontSize: isMobile ? '0.85rem' : '0.875rem',
            }}
          >
            Client: {client?.fullName || 'Unknown'}
          </Typography>
        </Box>

        {/* Mobile Action Buttons */}
        {isMobile && (
          <Box sx={{ display: 'flex', gap: 1, mt: 1, pt: 1, borderTop: '1px solid #e0e0e0' }}>
            <IconButton
              onClick={handleCall}
              disabled={!phoneNumber}
              sx={{
                flex: 1,
                backgroundColor: phoneNumber ? '#4caf50' : '#e0e0e0',
                color: '#fff',
                borderRadius: 2,
                py: 1,
                '&:hover': { backgroundColor: phoneNumber ? '#43a047' : '#e0e0e0' },
                '&:disabled': { color: '#999' },
              }}
            >
              <PhoneIcon sx={{ mr: 1 }} />
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Call
              </Typography>
            </IconButton>
            <IconButton
              onClick={handleNavigate}
              disabled={!property?.address}
              sx={{
                flex: 1,
                backgroundColor: property?.address ? '#1976d2' : '#e0e0e0',
                color: '#fff',
                borderRadius: 2,
                py: 1,
                '&:hover': { backgroundColor: property?.address ? '#1565c0' : '#e0e0e0' },
                '&:disabled': { color: '#999' },
              }}
            >
              <NavigationIcon sx={{ mr: 1 }} />
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Navigate
              </Typography>
            </IconButton>
          </Box>
        )}
      </CardContent>
    </Card>
  );

  // Wrap with Tooltip if jobOverview exists (desktop only)
  if (jobOverview && !isMobile) {
    return (
      <Tooltip
        title={
          <Box sx={{ maxWidth: 400, p: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Job Overview:
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {jobOverview}
            </Typography>
          </Box>
        }
        arrow
        placement="top"
        enterDelay={300}
      >
        {cardContent}
      </Tooltip>
    );
  }

  return cardContent;
};

// Day View Component
const InstallationDayView = ({ date, installations, onCardClick, isMobile, onOrderClick, onCheckClick, serialsMap }) => {
  // Group by time
  const grouped = useMemo(() => {
    const groups = { 'AM': [], 'PM': [], 'Full Day': [] };
    installations.forEach(inst => {
      const time = inst.installationTime || 'AM';
      if (groups[time]) {
        groups[time].push(inst);
      }
    });
    return groups;
  }, [installations]);

  const dayTotals = useMemo(() => {
    let fdInstallers = 0, fdTrucks = 0;
    let amInstallers = 0, amTrucks = 0;
    let pmInstallers = 0, pmTrucks = 0;
    installations.forEach(inst => {
      if (inst._isLegacy) return;
      const w = Number(inst.installersCount ?? 1);
      const t = Number(inst.trucksCount ?? 1);
      const time = inst.installationTime || 'AM';
      if (time === 'Full Day') { fdInstallers += w; fdTrucks += t; }
      else if (time === 'PM')  { pmInstallers += w; pmTrucks += t; }
      else                     { amInstallers += w; amTrucks += t; }
    });
    const installers = fdInstallers + Math.max(amInstallers, pmInstallers);
    const trucks = fdTrucks + Math.max(amTrucks, pmTrucks);
    const freeAmTrucks = Math.max(pmTrucks - amTrucks, 0);
    const freePmTrucks = Math.max(amTrucks - pmTrucks, 0);
    const freeAmInstallers = Math.max(pmInstallers - amInstallers, 0);
    const freePmInstallers = Math.max(amInstallers - pmInstallers, 0);
    return { installers, trucks, freeAmTrucks, freePmTrucks, freeAmInstallers, freePmInstallers };
  }, [installations]);

  return (
    <Box sx={{ mt: isMobile ? 2 : 3 }}>
      {/* Day totals */}
      {installations.length > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mb: 1, gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#555' }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>Day Total:</Typography>
          </Box>
          <Tooltip title={`${installations.length} Job(s)`} arrow>
            <Chip label={`${installations.length} jobs`} size="small" variant="outlined" />
          </Tooltip>
          <Tooltip title={`${dayTotals.installers} Installer(s) needed`} arrow>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#555' }}>
              <PeopleIcon sx={{ fontSize: 20 }} />
              <Typography variant="body2" sx={{ fontWeight: 700 }}>{dayTotals.installers}</Typography>
            </Box>
          </Tooltip>
          <Tooltip title={`${dayTotals.trucks} Truck(s) needed`} arrow>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#555' }}>
              <LocalShippingIcon sx={{ fontSize: 20 }} />
              <Typography variant="body2" sx={{ fontWeight: 700 }}>{dayTotals.trucks}</Typography>
            </Box>
          </Tooltip>
          {(dayTotals.freePmTrucks > 0 || dayTotals.freeAmTrucks > 0) && (
            <Tooltip
              title={
                dayTotals.freePmTrucks > 0
                  ? `${dayTotals.freePmTrucks} truck(s) / ${dayTotals.freePmInstallers} installer(s) available PM`
                  : `${dayTotals.freeAmTrucks} truck(s) / ${dayTotals.freeAmInstallers} installer(s) available AM`
              }
              arrow
            >
              <Chip
                label={
                  dayTotals.freePmTrucks > 0
                    ? `${dayTotals.freePmTrucks} 🚛 free PM`
                    : `${dayTotals.freeAmTrucks} 🚛 free AM`
                }
                size="small"
                sx={{ backgroundColor: '#fff3e0', color: '#ef6c00', fontWeight: 600, fontSize: '0.75rem' }}
              />
            </Tooltip>
          )}
        </Box>
      )}

      {/* AM Section */}
      {grouped['AM'].length > 0 && (
        <Box sx={{ mb: isMobile ? 3 : 4 }}>
          <Typography variant={isMobile ? "subtitle1" : "h6"} sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
            <AccessTimeIcon sx={{ mr: 1, color: '#1565c0', fontSize: isMobile ? 20 : 24 }} />
            Morning (AM)
            <Chip label={grouped['AM'].length} size="small" sx={{ ml: 1 }} />
          </Typography>
          <Grid container spacing={isMobile ? 1.5 : 2}>
            {grouped['AM'].map((inst) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={inst.id}>
                <InstallationCard installation={inst} onClick={onCardClick} isMobile={isMobile} onOrderClick={onOrderClick} onCheckClick={onCheckClick} serialStatus={serialsMap?.[getWarehousePO(inst)] || 'none'} />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* PM Section */}
      {grouped['PM'].length > 0 && (
        <Box sx={{ mb: isMobile ? 3 : 4 }}>
          <Typography variant={isMobile ? "subtitle1" : "h6"} sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
            <AccessTimeIcon sx={{ mr: 1, color: '#ef6c00', fontSize: isMobile ? 20 : 24 }} />
            Afternoon (PM)
            <Chip label={grouped['PM'].length} size="small" sx={{ ml: 1 }} />
          </Typography>
          <Grid container spacing={isMobile ? 1.5 : 2}>
            {grouped['PM'].map((inst) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={inst.id}>
                <InstallationCard installation={inst} onClick={onCardClick} isMobile={isMobile} onOrderClick={onOrderClick} onCheckClick={onCheckClick} serialStatus={serialsMap?.[getWarehousePO(inst)] || 'none'} />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Full Day Section */}
      {grouped['Full Day'].length > 0 && (
        <Box sx={{ mb: isMobile ? 3 : 4 }}>
          <Typography variant={isMobile ? "subtitle1" : "h6"} sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
            <AccessTimeIcon sx={{ mr: 1, color: '#7b1fa2', fontSize: isMobile ? 20 : 24 }} />
            Full Day
            <Chip label={grouped['Full Day'].length} size="small" sx={{ ml: 1 }} />
          </Typography>
          <Grid container spacing={isMobile ? 1.5 : 2}>
            {grouped['Full Day'].map((inst) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={inst.id}>
                <InstallationCard installation={inst} onClick={onCardClick} isMobile={isMobile} onOrderClick={onOrderClick} onCheckClick={onCheckClick} serialStatus={serialsMap?.[getWarehousePO(inst)] || 'none'} />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Empty state */}
      {installations.length === 0 && (
        <Paper sx={{ p: isMobile ? 3 : 4, textAlign: 'center', backgroundColor: '#f5f5f5' }}>
          <Typography variant={isMobile ? "body1" : "h6"} color="text.secondary">
            No installations scheduled for this day
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

// Week View Component — starts from selected date, shows 7 columns,
// weekends without installations are skipped
const InstallationWeekView = ({ date, installations, onCardClick, onOrderClick, onCheckClick, serialsMap }) => {
  const installationDates = useMemo(() => {
    const dates = new Set();
    installations.forEach(inst => {
      dates.add(moment(inst.date).format('YYYY-MM-DD'));
    });
    return dates;
  }, [installations]);

  const weekDays = useMemo(() => {
    const start = moment(date).startOf('day');
    const days = [start.clone()];
    let cursor = start.clone().add(1, 'day');
    while (days.length < 7) {
      const dow = cursor.day(); // 0=Sun, 6=Sat
      const isWeekend = dow === 0 || dow === 6;
      const dayStr = cursor.format('YYYY-MM-DD');
      if (!isWeekend || installationDates.has(dayStr)) {
        days.push(cursor.clone());
      }
      cursor.add(1, 'day');
    }
    return days;
  }, [date, installationDates]);

  const installationsByDay = useMemo(() => {
    const byDay = {};
    weekDays.forEach(day => {
      const dayStr = day.format('YYYY-MM-DD');
      byDay[dayStr] = installations.filter(inst => 
        moment(inst.date).format('YYYY-MM-DD') === dayStr
      );
    });
    return byDay;
  }, [installations, weekDays]);

  return (
    <Box sx={{ mt: 3 }}>
      <Grid container spacing={2}>
        {weekDays.map(day => {
          const dayStr = day.format('YYYY-MM-DD');
          const dayInstallations = installationsByDay[dayStr] || [];
          const isToday = day.isSame(moment(), 'day');

          return (
            <Grid item xs={12} sm={6} md={12/7} key={dayStr}>
              <Paper
                sx={{
                  p: 1.5,
                  minHeight: 200,
                  backgroundColor: isToday ? '#e3f2fd' : '#fff',
                  border: isToday ? '2px solid #1976d2' : '1px solid #e0e0e0',
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 600,
                      color: isToday ? '#1976d2' : '#333',
                    }}
                  >
                    {day.format('ddd')}
                    <br />
                    {day.format('MMM D')}
                  </Typography>
                  {dayInstallations.length > 0 && (() => {
                    let fdW = 0, fdT = 0, amW = 0, amT = 0, pmW = 0, pmT = 0;
                    dayInstallations.forEach(i => {
                      const w = Number(i.installersCount ?? 1), t = Number(i.trucksCount ?? 1);
                      const tm = i.installationTime || 'AM';
                      if (tm === 'Full Day') { fdW += w; fdT += t; }
                      else if (tm === 'PM')  { pmW += w; pmT += t; }
                      else                   { amW += w; amT += t; }
                    });
                    return (
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.25 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: '#555', fontSize: '0.65rem' }}>
                          {dayInstallations.length} job{dayInstallations.length !== 1 ? 's' : ''}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, color: '#666' }}>
                            <PeopleIcon sx={{ fontSize: 12 }} />
                            <Typography variant="caption" sx={{ fontSize: '0.6rem', fontWeight: 700 }}>
                              {fdW + Math.max(amW, pmW)}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, color: '#666' }}>
                            <LocalShippingIcon sx={{ fontSize: 12 }} />
                            <Typography variant="caption" sx={{ fontSize: '0.6rem', fontWeight: 700 }}>
                              {fdT + Math.max(amT, pmT)}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    );
                  })()}
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {dayInstallations.map(inst => {
                    const instIsLegacy = !!inst._isLegacy;
                    const hasEquip = instIsLegacy ? false : proposalHasEquipment(inst.proposal);
                    const allOrd = hasEquip && isAllEquipmentOrdered(inst.proposal);
                    const instIsWarranty = !instIsLegacy && isWarrantyProposal(inst.proposal);
                    const serStatus = serialsMap?.[getWarehousePO(inst)] || 'none';
                    const showWh = instIsLegacy || hasEquip || instIsWarranty;
                    return (
                      <Box
                        key={inst.id}
                        onClick={() => {
                          if (instIsLegacy) {
                            if (inst.legacyUrl) window.open(inst.legacyUrl, '_blank');
                          } else {
                            onCardClick(inst.proposal?.id);
                          }
                        }}
                        sx={{
                          p: 1,
                          borderRadius: 1,
                          backgroundColor: instIsLegacy ? '#fff8e1' : (timeColors[inst.installationTime]?.bg || '#f5f5f5'),
                          border: instIsLegacy ? '1px solid #e65100' : 'none',
                          cursor: 'pointer',
                          '&:hover': { opacity: 0.8 },
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="caption" sx={{ fontWeight: 600, color: instIsLegacy ? '#e65100' : 'inherit' }}>
                              {inst.proposal?.publicId}
                            </Typography>
                            {instIsLegacy && (
                              <Typography variant="caption" sx={{ fontSize: '0.55rem', fontWeight: 700, color: '#e65100' }}>
                                LEGACY
                              </Typography>
                            )}
                          </Box>
                          {hasEquip && !instIsLegacy && (
                            <Tooltip title={allOrd ? 'All equipment ordered' : 'Order Equipment'} arrow>
                              <IconButton
                                size="small"
                                onClick={(e) => { e.stopPropagation(); onOrderClick?.(inst); }}
                                sx={{ p: 0.25 }}
                              >
                                <LocalShippingIcon sx={{ fontSize: 14, color: allOrd ? '#4caf50' : '#9e9e9e' }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          {showWh && (
                            <Tooltip title={serStatus === 'all' ? 'All equipment has serial numbers' : serStatus === 'partial' ? 'Some equipment missing serial numbers' : 'Check Equipment'} arrow>
                              <IconButton
                                size="small"
                                onClick={(e) => { e.stopPropagation(); onCheckClick?.(inst); }}
                                sx={{ p: 0.25 }}
                              >
                                <Inventory2Icon sx={{ fontSize: 14, color: serStatus === 'all' ? '#4caf50' : serStatus === 'partial' ? '#ff9800' : '#9e9e9e' }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#666' }}>
                            {inst.installationTime || ''}
                          </Typography>
                          {!instIsLegacy && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, color: '#666' }}>
                                <PeopleIcon sx={{ fontSize: 12 }} />
                                <Typography variant="caption" sx={{ fontSize: '0.6rem', fontWeight: 600 }}>
                                  {inst.installersCount ?? 1}
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, color: '#666' }}>
                                <LocalShippingIcon sx={{ fontSize: 12 }} />
                                <Typography variant="caption" sx={{ fontSize: '0.6rem', fontWeight: 600 }}>
                                  {inst.trucksCount ?? 1}
                                </Typography>
                              </Box>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                  {dayInstallations.length === 0 && (
                    <Typography variant="caption" sx={{ color: '#999', textAlign: 'center' }}>
                      No installations
                    </Typography>
                  )}
                </Box>
              </Paper>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

// Month View Component
const InstallationMonthView = ({ date, installations, onCardClick, onDayClick }) => {
  const monthData = useMemo(() => {
    const startOfMonth = moment(date).startOf('month');
    const endOfMonth = moment(date).endOf('month');
    const startOfCalendar = startOfMonth.clone().startOf('week');
    const endOfCalendar = endOfMonth.clone().endOf('week');
    
    const weeks = [];
    let current = startOfCalendar.clone();
    
    while (current.isSameOrBefore(endOfCalendar)) {
      const week = [];
      for (let i = 0; i < 7; i++) {
        week.push(current.clone());
        current.add(1, 'day');
      }
      weeks.push(week);
    }
    
    return weeks;
  }, [date]);

  const installationsByDay = useMemo(() => {
    const byDay = {};
    installations.forEach(inst => {
      const dayStr = moment(inst.date).format('YYYY-MM-DD');
      if (!byDay[dayStr]) byDay[dayStr] = [];
      byDay[dayStr].push(inst);
    });
    return byDay;
  }, [installations]);

  return (
    <Box sx={{ mt: 3 }}>
      {/* Header */}
      <Grid container sx={{ mb: 1 }}>
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
          <Grid item xs={12/7} key={day}>
            <Typography variant="subtitle2" sx={{ textAlign: 'center', fontWeight: 600, color: '#666' }}>
              {day}
            </Typography>
          </Grid>
        ))}
      </Grid>

      {/* Calendar Grid */}
      {monthData.map((week, weekIdx) => (
        <Grid container key={weekIdx} sx={{ borderTop: '1px solid #e0e0e0' }}>
          {week.map(day => {
            const dayStr = day.format('YYYY-MM-DD');
            const dayInstallations = installationsByDay[dayStr] || [];
            const isCurrentMonth = day.month() === moment(date).month();
            const isToday = day.isSame(moment(), 'day');

            return (
              <Grid
                item
                xs={12/7}
                key={dayStr}
                sx={{
                  minHeight: 100,
                  borderRight: '1px solid #e0e0e0',
                  borderBottom: '1px solid #e0e0e0',
                  p: 0.5,
                  backgroundColor: isToday ? '#e3f2fd' : isCurrentMonth ? '#fff' : '#f9f9f9',
                  cursor: 'pointer',
                  '&:hover': { backgroundColor: '#f0f0f0' },
                }}
                onClick={() => onDayClick(day)}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: isToday ? 700 : 400,
                    color: isCurrentMonth ? '#333' : '#999',
                  }}
                >
                  {day.format('D')}
                </Typography>
                {dayInstallations.slice(0, 3).map(inst => (
                  <Box
                    key={inst.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (inst._isLegacy) {
                        if (inst.legacyUrl) window.open(inst.legacyUrl, '_blank');
                      } else {
                        onCardClick(inst.proposal?.id);
                      }
                    }}
                    sx={{
                      p: 0.25,
                      mb: 0.25,
                      borderRadius: 0.5,
                      backgroundColor: inst._isLegacy ? '#fff8e1' : (timeColors[inst.installationTime]?.bg || '#f5f5f5'),
                      border: inst._isLegacy ? '1px solid #e65100' : 'none',
                      cursor: 'pointer',
                    }}
                  >
                    <Typography variant="caption" sx={{ fontSize: '0.6rem', fontWeight: 500, color: inst._isLegacy ? '#e65100' : 'inherit' }}>
                      {inst.proposal?.publicId}{inst._isLegacy ? ' L' : ''}
                    </Typography>
                  </Box>
                ))}
                {dayInstallations.length > 3 && (
                  <Typography variant="caption" sx={{ fontSize: '0.6rem', color: '#666' }}>
                    +{dayInstallations.length - 3} more
                  </Typography>
                )}
              </Grid>
            );
          })}
        </Grid>
      ))}
    </Box>
  );
};

// Main Page Component
export const Installations = () => {
  const [date, setDate] = useState(() => {
    return sessionStorage.getItem('installations_date') || moment().format();
  });
  const [view, setView] = useState(() => {
    return sessionStorage.getItem('installations_view') || 'day';
  });
  const [installations, setInstallations] = useState(null);
  const [loading, setLoading] = useState(false);
  const [serialsMap, setSerialsMap] = useState({});

  // Equipment modal state
  const [orderModalInst, setOrderModalInst] = useState(null);
  const [checkModalInst, setCheckModalInst] = useState(null);

  useEffect(() => {
    sessionStorage.setItem('installations_date', date);
  }, [date]);

  useEffect(() => {
    sessionStorage.setItem('installations_view', view);
  }, [view]);

  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Load installations based on view (native + legacy in parallel)
  const loadInstallations = useCallback(async () => {
    setLoading(true);
    try {
      let startDate, endDate;
      
      if (view === 'day') {
        startDate = moment(date).format('YYYY-MM-DD');
        endDate = startDate;
      } else if (view === 'week') {
        startDate = moment(date).format('YYYY-MM-DD');
        endDate = moment(date).add(13, 'days').format('YYYY-MM-DD');
      } else {
        startDate = moment(date).startOf('month').startOf('week').format('YYYY-MM-DD');
        endDate = moment(date).endOf('month').endOf('week').format('YYYY-MM-DD');
      }

      const [nativeResponse, legacyResponse] = await Promise.all([
        installationScheduleServices.getByDateRange(startDate, endDate),
        integrationServices.getLegacyInstallations(startDate, endDate).catch(() => ({ data: [] })),
      ]);

      const raw = nativeResponse.data || [];
      const seen = new Set();
      const deduped = raw.filter((inst) => {
        const key = `${inst.proposal?.publicId}_${inst.date}_${inst.installationTime}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });

      const nativePublicIds = new Set(deduped.map(inst => inst.proposal?.publicId).filter(Boolean));
      const legacyItems = (legacyResponse.data || []).filter(
        (inst) => !nativePublicIds.has(inst.proposal?.publicId)
      );

      setInstallations([...deduped, ...legacyItems]);
    } catch (error) {
      toast.error(messages.errors.error_data_loaning);
      console.error(error);
      setInstallations([]);
    } finally {
      setLoading(false);
    }
  }, [date, view]);

  useEffect(() => {
    loadInstallations();
  }, [loadInstallations]);

  // Load warehouse serial number status for visible installations (native with equipment + all legacy)
  useEffect(() => {
    if (!installations || installations.length === 0) return;

    const poNumbers = [...new Set(
      installations
        .filter(inst => inst._isLegacy || proposalHasEquipment(inst.proposal) || isWarrantyProposal(inst.proposal))
        .map(inst => getWarehousePO(inst))
        .filter(Boolean)
    )];

    if (poNumbers.length === 0) return;

    integrationServices.batchSerialCheck(poNumbers)
      .then(res => setSerialsMap(res.data || {}))
      .catch(() => {});
  }, [installations]);

  // Listen for real-time installation changes via WebSocket
  useInstallationSocket(
    useCallback((data) => {
      console.log('[Installations] WebSocket event received:', data);
      // Reload installations when any change occurs
      loadInstallations();
    }, [loadInstallations])
  );

  const handleCardClick = (proposalId) => {
    if (proposalId) {
      navigate(`/proposals/view/${proposalId}`);
    }
  };

  const handleDayClick = (day) => {
    setDate(day.format());
    setView('day');
  };

  const handleViewChange = (newView) => {
    setView(newView);
  };

  // Print all installations on current page
  const handlePrintAll = () => {
    if (!installations || installations.length === 0) {
      toast.warning('No installations to print');
      return;
    }

    // Get unique proposal IDs from current installations
    const proposalIds = [...new Set(
      installations
        .map(inst => inst.proposal?.id)
        .filter(id => id)
    )];

    if (proposalIds.length === 0) {
      toast.warning('No proposals found for printing');
      return;
    }

    if (proposalIds.length > 50) {
      toast.error(`Maximum 50 proposals can be printed at once. Current view has ${proposalIds.length}.`);
      return;
    }

    const ids = proposalIds.join(',');
    const dateParam = view === 'day' ? `&installDate=${moment(date).format('YYYY-MM-DD')}` : '';
    window.open(`/proposals/print-batch?ids=${ids}${dateParam}`, '_blank');
  };

  const handleOrderClick = useCallback((installation) => {
    setOrderModalInst(installation);
  }, []);

  const handleCheckClick = useCallback((installation) => {
    setCheckModalInst(installation);
  }, []);

  const handleSaveNotes = useCallback(async (notes) => {
    if (!orderModalInst?.proposal?.id) return;
    try {
      await proposalsServices.update(orderModalInst.proposal.id, { additionalNotes: notes });
    } catch (err) {
      console.error('Failed to save notes:', err);
    }
  }, [orderModalInst]);

  // Calculate total count for current view
  const totalCount = installations?.length || 0;

  if (installations === null) return <Spinner />;

  return (
    <Box component="section" id="installations-page" sx={{ p: isMobile ? 1 : 2 }}>
      <BrowserTitle title="Installations | Airmaxx Pro" />

      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 2, 
        flexWrap: 'wrap', 
        gap: isMobile ? 1 : 2 
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: isMobile ? 1 : 2 }}>
          <Typography variant={isMobile ? "h6" : "h5"} sx={{ fontWeight: 600 }}>
            {isMobile ? 'My Installations' : 'Installations'}
          </Typography>
          <Chip
            label={totalCount}
            color="primary"
            size={isMobile ? "small" : "medium"}
            variant="outlined"
          />
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: isMobile ? 1 : 2 }}>
          {!isMobile && (
            <Button
              variant="contained"
              startIcon={<PrintIcon />}
              onClick={handlePrintAll}
              disabled={!installations || installations.length === 0}
              sx={{
                backgroundColor: '#1976d2',
                '&:hover': { backgroundColor: '#1565c0' },
                '&:disabled': { backgroundColor: '#ccc' },
              }}
            >
              Print All ({totalCount})
            </Button>
          )}
          {!isMobile && <ViewSwitcher view={view} setView={handleViewChange} />}
          <DateSwitcher date={date} setDate={setDate} mode={view === 'month' ? 'month' : 'day'} />
        </Box>
      </Box>

      {/* Content */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <Spinner />
        </Box>
      ) : (
        <>
          {view === 'day' && (
            <InstallationDayView
              date={date}
              installations={installations}
              onCardClick={handleCardClick}
              isMobile={isMobile}
              onOrderClick={handleOrderClick}
              onCheckClick={handleCheckClick}
              serialsMap={serialsMap}
            />
          )}
          {view === 'week' && !isMobile && (
            <InstallationWeekView
              date={date}
              installations={installations}
              onCardClick={handleCardClick}
              onOrderClick={handleOrderClick}
              onCheckClick={handleCheckClick}
              serialsMap={serialsMap}
            />
          )}
          {view === 'week' && isMobile && (
            <InstallationDayView
              date={date}
              installations={installations}
              onCardClick={handleCardClick}
              isMobile={isMobile}
              onOrderClick={handleOrderClick}
              onCheckClick={handleCheckClick}
              serialsMap={serialsMap}
            />
          )}
          {view === 'month' && !isMobile && (
            <InstallationMonthView
              date={date}
              installations={installations}
              onCardClick={handleCardClick}
              onDayClick={handleDayClick}
            />
          )}
          {view === 'month' && isMobile && (
            <InstallationDayView
              date={date}
              installations={installations}
              onCardClick={handleCardClick}
              isMobile={isMobile}
              onOrderClick={handleOrderClick}
              onCheckClick={handleCheckClick}
              serialsMap={serialsMap}
            />
          )}
        </>
      )}

      {/* Equipment Modals */}
      {orderModalInst && (
        <OrderEquipmentModal
          open={!!orderModalInst}
          onClose={() => { setOrderModalInst(null); loadInstallations(); }}
          systems={orderModalInst.proposal?.proposalSystems || []}
          proposal={orderModalInst.proposal}
          orders={orderModalInst.proposal?.equipmentOrders || []}
          publicId={orderModalInst.proposal?.publicId}
          proposalId={orderModalInst.proposal?.id}
          refresh={loadInstallations}
          additionalNotes={orderModalInst.proposal?.additionalNotes}
          onSaveNotes={handleSaveNotes}
          installationSchedule={[{ date: orderModalInst.date }]}
          createdBy={orderModalInst.proposal?.createdBy}
        />
      )}

      <CheckEquipmentModal
        open={!!checkModalInst}
        onClose={() => setCheckModalInst(null)}
        publicId={checkModalInst ? getWarehousePO(checkModalInst) : undefined}
      />
    </Box>
  );
};

export default Installations;

